import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixEditorBranchesV1Branch from './editor-branches-v1-branch-branches.http.js';
// @ts-ignore
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

/**
 * A branch is a version of draft site content that can be edited independently. Also known as a test site.
 *
 * Branches let site owners and developers create isolated versions of a site for testing changes without affecting the live site. Each branch maintains its own content, design, and settings while sharing the same site infrastructure.
 *
 * Learn more about [branches](https://support.wix.com/en/article/about-test-sites).
 */
export interface Branch extends BranchSourceOneOf {
  /** Source branch properties. Use when `sourceType` = `SOURCE_BRANCH`. */
  sourceBranchProperties?: SourceBranch;
  /** Source template properties. Use when `sourceType` = `SOURCE_TEMPLATE`. */
  sourceTemplateProperties?: SourceTemplate;
  /**
   * Branch ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the branch is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the branch.
   * @readonly
   */
  revision?: string | null;
  /** Branch type. */
  type?: TypeWithLiterals;
  /**
   * Branch name.
   * @maxLength 50
   */
  name?: string | null;
  /** Source type for this branch's content. Must be aligned with the corresponding source properties below. */
  sourceType?: SourceTypeWithLiterals;
  /**
   * Date and time the branch was last published.
   * @readonly
   */
  lastPublishDate?: string | null;
  /**
   * Wix editor type.
   * @readonly
   */
  editorType?: EditorType;
  /**
   * Whether this branch is the default branch. The default branch is used for API operations when no branch ID is specified.
   * @readonly
   */
  default?: boolean | null;
  /**
   * Date and time the branch was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the branch was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Custom field data for the branch.
   * [Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.
   */
  extendedFields?: ExtendedFields;
  /** Public tags assigned to the branch. */
  tags?: PublicTags;
}

/** @oneof */
export interface BranchSourceOneOf {
  /** Source branch properties. Use when `sourceType` = `SOURCE_BRANCH`. */
  sourceBranchProperties?: SourceBranch;
  /** Source template properties. Use when `sourceType` = `SOURCE_TEMPLATE`. */
  sourceTemplateProperties?: SourceTemplate;
}

export enum Type {
  /** A branch created by a site owner that can be accessed for editing and publishing in the editor. */
  USER = 'USER',
  /** A branch created for technical purposes, unrelated to site owners. For internal use, can't be accessed in the editor. */
  TECHNICAL = 'TECHNICAL',
  /** The original branch of the site, created when the site is created. Can be accessed for editing and publishing in the editor. */
  ORIGINAL_BRANCH = 'ORIGINAL_BRANCH',
}

/** @enumType */
export type TypeWithLiterals = Type | 'USER' | 'TECHNICAL' | 'ORIGINAL_BRANCH';

export enum SourceType {
  /** This branch's source was another branch. Must be used with `sourceBranchProperties`. */
  SOURCE_BRANCH = 'SOURCE_BRANCH',
}

/** @enumType */
export type SourceTypeWithLiterals = SourceType | 'SOURCE_BRANCH';

export interface EditorType {
  /** Wix editor types. */
  editorTypeOptions?: EditorTypeOptionsWithLiterals;
}

export enum EditorTypeOptions {
  /** Wix Studio Editor. */
  STUDIO = 'STUDIO',
  /** Wix's Classic Editor. */
  CLASSIC = 'CLASSIC',
}

/** @enumType */
export type EditorTypeOptionsWithLiterals =
  | EditorTypeOptions
  | 'STUDIO'
  | 'CLASSIC';

export interface SourceBranch {
  /**
   * Source branch ID.
   * @format GUID
   */
  branchId?: string;
  /** Revision number of the source branch when this branch was created. To prevent conflicting changes, the current revision must be passed when updating the branch. */
  branchRevision?: string | null;
}

export interface SourceTemplate {
  /**
   * Source template ID.
   * @format GUID
   */
  templateId?: string;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface PublicTags {
  /** Tags that are exposed to anyone who has access to the labeled entity itself, including site members and visitors. */
  tags?: TagList;
}

export interface TagList {
  /**
   * List of tag IDs.
   * @maxSize 100
   * @maxLength 5
   */
  tagIds?: string[];
}

export interface TagsModified {
  /** Updated branch. */
  branch?: Branch;
  /** Tags that were assigned to the branch. */
  assignedTags?: PublicTags;
  /** Tags that were unassigned from the branch. */
  unassignedTags?: PublicTags;
}

export interface CreateBranchRequest {
  /** Branch to create. */
  branch: Branch;
}

export interface CreateBranchResponse {
  /** Created branch. */
  branch?: Branch;
}

export interface GetBranchRequest {
  /**
   * ID of the branch to retrieve.
   * @format GUID
   */
  branchId: string;
}

export interface GetBranchResponse {
  /** Requested branch. */
  branch?: Branch;
}

export interface UpdateBranchRequest {
  /** Branch to update. */
  branch: Branch;
}

export interface UpdateBranchResponse {
  /** Updated branch. */
  branch?: Branch;
}

export interface DeleteBranchRequest {
  /**
   * ID of the branch to delete.
   * @format GUID
   */
  branchId: string;
}

export interface DeleteBranchResponse {}

export interface QueryBranchesRequest {
  /** Query options. See [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language) for more information. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language#sorting).
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryBranchesResponse {
  /** List of branches. */
  branches?: Branch[];
  /** Metadata for the paginated results. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface UpdateExtendedFieldsRequest {
  /** ID of the entity to update. */
  _id?: string;
  /** Identifier for the app whose extended fields are being updated. */
  namespace?: string;
  /** Data to update. Structured according to the [schema](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields#json-schema-for-extended-fields) defined when the extended fields were configured. */
  namespaceData?: Record<string, any> | null;
}

export interface UpdateExtendedFieldsResponse {
  /** Updated branch. */
  branch?: Branch;
}

export interface HtmlSitePublished {
  /**
   * Application instance ID
   * @maxLength 50
   */
  appInstanceId?: string;
  /**
   * Application type
   * @maxLength 100
   */
  appType?: string;
  /** Revision */
  revision?: string;
  /**
   * MSID
   * @maxLength 100
   */
  metaSiteId?: string | null;
  /**
   * optional branch id if publish is done from branch
   * @format GUID
   */
  branchId?: string | null;
  /** The site's last transactionId */
  lastTransactionId?: string | null;
  /** A list of the site's pages */
  pages?: Page[];
  /** Site's publish date */
  publishDate?: string;
  /** Determine if this message was produced by editor-deployments service */
  originatedFromDeploymentsService?: boolean;
}

export interface Page {
  /**
   * Page's Id
   * @maxLength 100
   */
  _id?: string;
}

export interface Empty {}

export interface MetaSiteSpecialEvent extends MetaSiteSpecialEventPayloadOneOf {
  /** Emitted on a meta site creation. */
  siteCreated?: SiteCreated;
  /** Emitted on a meta site transfer completion. */
  siteTransferred?: SiteTransferred;
  /** Emitted on a meta site deletion. */
  siteDeleted?: SiteDeleted;
  /** Emitted on a meta site restoration. */
  siteUndeleted?: SiteUndeleted;
  /** Emitted on the first* publish of the meta site (* switching from unpublished to published state). */
  sitePublished?: SitePublished;
  /** Emitted on a meta site unpublish. */
  siteUnpublished?: SiteUnpublished;
  /** Emitted when meta site is marked as template. */
  siteMarkedAsTemplate?: SiteMarkedAsTemplate;
  /** Emitted when meta site is marked as a WixSite. */
  siteMarkedAsWixSite?: SiteMarkedAsWixSite;
  /** Emitted when an application is provisioned (installed). */
  serviceProvisioned?: ServiceProvisioned;
  /** Emitted when an application is removed (uninstalled). */
  serviceRemoved?: ServiceRemoved;
  /** Emitted when meta site name (URL slug) is changed. */
  siteRenamedPayload?: SiteRenamed;
  /** Emitted when meta site was permanently deleted. */
  hardDeleted?: SiteHardDeleted;
  /** Emitted on a namespace change. */
  namespaceChanged?: NamespaceChanged;
  /** Emitted when Studio is attached. */
  studioAssigned?: StudioAssigned;
  /** Emitted when Studio is detached. */
  studioUnassigned?: StudioUnassigned;
  /**
   * Emitted when one of the URLs is changed. After this event you may call `urls-server` to fetch
   * the actual URL.
   *
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1732520791210559?thread_ts=1732027914.294059&cid=C0UHEBPFT
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1744115197619459
   */
  urlChanged?: SiteUrlChanged;
  /** Site is marked as PurgedExternally */
  sitePurgedExternally?: SitePurgedExternally;
  /** Emitted when Odeditor is attached. */
  odeditorAssigned?: OdeditorAssigned;
  /** Emitted when Odeditor is detached. */
  odeditorUnassigned?: OdeditorUnassigned;
  /** Emitted when Picasso is attached. */
  picassoAssigned?: PicassoAssigned;
  /** Emitted when Picasso is detached. */
  picassoUnassigned?: PicassoUnassigned;
  /** Emitted when Wixel is attached. */
  wixelAssigned?: WixelAssigned;
  /** Emitted when Wixel is detached. */
  wixelUnassigned?: WixelUnassigned;
  /** Emitted when StudioTwo is attached. */
  studioTwoAssigned?: StudioTwoAssigned;
  /** Emitted when StudioTwo is detached. */
  studioTwoUnassigned?: StudioTwoUnassigned;
  /** Emitted when media from user domain is enabled. */
  userDomainMediaEnabled?: UserDomainMediaEnabled;
  /** Emitted when media from user domain is disabled. */
  userDomainMediaDisabled?: UserDomainMediaDisabled;
  /** Emitted when Editorless is attached. */
  editorlessAssigned?: EditorlessAssigned;
  /** Emitted when Editorless is detached. */
  editorlessUnassigned?: EditorlessUnassigned;
  /**
   * A meta site id.
   * @format GUID
   */
  metaSiteId?: string;
  /** A meta site version. Monotonically increasing. */
  version?: string;
  /** A timestamp of the event. */
  timestamp?: string;
  /**
   * TODO(meta-site): Change validation once validations are disabled for consumers
   * More context: https://wix.slack.com/archives/C0UHEBPFT/p1720957844413149 and https://wix.slack.com/archives/CFWKX325T/p1728892152855659
   * @maxSize 4000
   */
  assets?: Asset[];
}

/** @oneof */
export interface MetaSiteSpecialEventPayloadOneOf {
  /** Emitted on a meta site creation. */
  siteCreated?: SiteCreated;
  /** Emitted on a meta site transfer completion. */
  siteTransferred?: SiteTransferred;
  /** Emitted on a meta site deletion. */
  siteDeleted?: SiteDeleted;
  /** Emitted on a meta site restoration. */
  siteUndeleted?: SiteUndeleted;
  /** Emitted on the first* publish of the meta site (* switching from unpublished to published state). */
  sitePublished?: SitePublished;
  /** Emitted on a meta site unpublish. */
  siteUnpublished?: SiteUnpublished;
  /** Emitted when meta site is marked as template. */
  siteMarkedAsTemplate?: SiteMarkedAsTemplate;
  /** Emitted when meta site is marked as a WixSite. */
  siteMarkedAsWixSite?: SiteMarkedAsWixSite;
  /** Emitted when an application is provisioned (installed). */
  serviceProvisioned?: ServiceProvisioned;
  /** Emitted when an application is removed (uninstalled). */
  serviceRemoved?: ServiceRemoved;
  /** Emitted when meta site name (URL slug) is changed. */
  siteRenamedPayload?: SiteRenamed;
  /** Emitted when meta site was permanently deleted. */
  hardDeleted?: SiteHardDeleted;
  /** Emitted on a namespace change. */
  namespaceChanged?: NamespaceChanged;
  /** Emitted when Studio is attached. */
  studioAssigned?: StudioAssigned;
  /** Emitted when Studio is detached. */
  studioUnassigned?: StudioUnassigned;
  /**
   * Emitted when one of the URLs is changed. After this event you may call `urls-server` to fetch
   * the actual URL.
   *
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1732520791210559?thread_ts=1732027914.294059&cid=C0UHEBPFT
   * See: https://wix.slack.com/archives/C0UHEBPFT/p1744115197619459
   */
  urlChanged?: SiteUrlChanged;
  /** Site is marked as PurgedExternally */
  sitePurgedExternally?: SitePurgedExternally;
  /** Emitted when Odeditor is attached. */
  odeditorAssigned?: OdeditorAssigned;
  /** Emitted when Odeditor is detached. */
  odeditorUnassigned?: OdeditorUnassigned;
  /** Emitted when Picasso is attached. */
  picassoAssigned?: PicassoAssigned;
  /** Emitted when Picasso is detached. */
  picassoUnassigned?: PicassoUnassigned;
  /** Emitted when Wixel is attached. */
  wixelAssigned?: WixelAssigned;
  /** Emitted when Wixel is detached. */
  wixelUnassigned?: WixelUnassigned;
  /** Emitted when StudioTwo is attached. */
  studioTwoAssigned?: StudioTwoAssigned;
  /** Emitted when StudioTwo is detached. */
  studioTwoUnassigned?: StudioTwoUnassigned;
  /** Emitted when media from user domain is enabled. */
  userDomainMediaEnabled?: UserDomainMediaEnabled;
  /** Emitted when media from user domain is disabled. */
  userDomainMediaDisabled?: UserDomainMediaDisabled;
  /** Emitted when Editorless is attached. */
  editorlessAssigned?: EditorlessAssigned;
  /** Emitted when Editorless is detached. */
  editorlessUnassigned?: EditorlessUnassigned;
}

export interface Asset {
  /**
   * An application definition id (app_id in dev-center). For legacy reasons may be UUID or a string (from Java Enum).
   * @maxLength 36
   */
  appDefId?: string;
  /**
   * An instance id. For legacy reasons may be UUID or a string.
   * @maxLength 200
   */
  instanceId?: string;
  /** An application state. */
  state?: StateWithLiterals;
}

export enum State {
  UNKNOWN = 'UNKNOWN',
  ENABLED = 'ENABLED',
  DISABLED = 'DISABLED',
  PENDING = 'PENDING',
  DEMO = 'DEMO',
}

/** @enumType */
export type StateWithLiterals =
  | State
  | 'UNKNOWN'
  | 'ENABLED'
  | 'DISABLED'
  | 'PENDING'
  | 'DEMO';

export interface SiteCreated {
  /**
   * A template identifier (empty if not created from a template).
   * @maxLength 36
   */
  originTemplateId?: string;
  /**
   * An account id of the owner.
   * @format GUID
   */
  ownerId?: string;
  /** A context in which meta site was created. */
  context?: SiteCreatedContextWithLiterals;
  /**
   * A meta site id from which this site was created.
   *
   * In case of a creation from a template it's a template id.
   * In case of a site duplication ("Save As" in dashboard or duplicate in UM) it's an id of a source site.
   * @format GUID
   */
  originMetaSiteId?: string | null;
  /**
   * A meta site name (URL slug).
   * @maxLength 20
   */
  siteName?: string;
  /** A namespace. */
  namespace?: NamespaceWithLiterals;
}

export enum SiteCreatedContext {
  /** A valid option, we don't expose all reasons why site might be created. */
  OTHER = 'OTHER',
  /** A meta site was created from template. */
  FROM_TEMPLATE = 'FROM_TEMPLATE',
  /** A meta site was created by copying of the transfferred meta site. */
  DUPLICATE_BY_SITE_TRANSFER = 'DUPLICATE_BY_SITE_TRANSFER',
  /** A copy of existing meta site. */
  DUPLICATE = 'DUPLICATE',
  /** A meta site was created as a transfferred site (copy of the original), old flow, should die soon. */
  OLD_SITE_TRANSFER = 'OLD_SITE_TRANSFER',
  /** deprecated A meta site was created for Flash editor. */
  FLASH = 'FLASH',
}

/** @enumType */
export type SiteCreatedContextWithLiterals =
  | SiteCreatedContext
  | 'OTHER'
  | 'FROM_TEMPLATE'
  | 'DUPLICATE_BY_SITE_TRANSFER'
  | 'DUPLICATE'
  | 'OLD_SITE_TRANSFER'
  | 'FLASH';

export enum Namespace {
  UNKNOWN_NAMESPACE = 'UNKNOWN_NAMESPACE',
  /** Default namespace for UGC sites. MetaSites with this namespace will be shown in a user's site list by default. */
  WIX = 'WIX',
  /** ShoutOut stand alone product. These are siteless (no actual Wix site, no HtmlWeb). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  SHOUT_OUT = 'SHOUT_OUT',
  /** MetaSites created by the Albums product, they appear as part of the Albums app. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  ALBUMS = 'ALBUMS',
  /** Part of the WixStores migration flow, a user tries to migrate and gets this site to view and if the user likes it then stores removes this namespace and deletes the old site with the old stores. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  WIX_STORES_TEST_DRIVE = 'WIX_STORES_TEST_DRIVE',
  /** Hotels standalone (siteless). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  HOTELS = 'HOTELS',
  /** Clubs siteless MetaSites, a club without a wix website. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  CLUBS = 'CLUBS',
  /** A partially created ADI website. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  ONBOARDING_DRAFT = 'ONBOARDING_DRAFT',
  /** AppBuilder for AppStudio / shmite (c). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  DEV_SITE = 'DEV_SITE',
  /** LogoMaker websites offered to the user after logo purchase. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  LOGOS = 'LOGOS',
  /** VideoMaker websites offered to the user after video purchase. MetaSites with this namespace will *not* be shown in a user's site list by default. */
  VIDEO_MAKER = 'VIDEO_MAKER',
  /** MetaSites with this namespace will *not* be shown in a user's site list by default. */
  PARTNER_DASHBOARD = 'PARTNER_DASHBOARD',
  /** MetaSites with this namespace will *not* be shown in a user's site list by default. */
  DEV_CENTER_COMPANY = 'DEV_CENTER_COMPANY',
  /**
   * A draft created by HTML editor on open. Upon "first save" it will be moved to be of WIX domain.
   *
   * Meta site with this namespace will *not* be shown in a user's site list by default.
   */
  HTML_DRAFT = 'HTML_DRAFT',
  /**
   * the user-journey for Fitness users who want to start from managing their business instead of designing their website.
   * Will be accessible from Site List and will not have a website app.
   * Once the user attaches a site, the site will become a regular wixsite.
   */
  SITELESS_BUSINESS = 'SITELESS_BUSINESS',
  /** Belongs to "strategic products" company. Supports new product in the creator's economy space. */
  CREATOR_ECONOMY = 'CREATOR_ECONOMY',
  /** It is to be used in the Business First efforts. */
  DASHBOARD_FIRST = 'DASHBOARD_FIRST',
  /** Bookings business flow with no site. */
  ANYWHERE = 'ANYWHERE',
  /** Namespace for Headless Backoffice with no editor */
  HEADLESS = 'HEADLESS',
  /**
   * Namespace for master site that will exist in parent account that will be referenced by subaccounts
   * The site will be used for account level CSM feature for enterprise
   */
  ACCOUNT_MASTER_CMS = 'ACCOUNT_MASTER_CMS',
  /** Rise.ai Siteless account management for Gift Cards and Store Credit. */
  RISE = 'RISE',
  /**
   * As part of the branded app new funnel, users now can create a meta site that will be branded app first.
   * There's a blank site behind the scene but it's blank).
   * The Mobile company will be the owner of this namespace.
   */
  BRANDED_FIRST = 'BRANDED_FIRST',
  /** Nownia.com Siteless account management for Ai Scheduling Assistant. */
  NOWNIA = 'NOWNIA',
  /**
   * UGC Templates are templates that are created by users for personal use and to sale to other users.
   * The Partners company owns this namespace.
   */
  UGC_TEMPLATE = 'UGC_TEMPLATE',
  /** Codux Headless Sites */
  CODUX = 'CODUX',
  /** Bobb - AI Design Creator. */
  MEDIA_DESIGN_CREATOR = 'MEDIA_DESIGN_CREATOR',
  /**
   * Shared Blog Site is a unique single site across Enterprise account,
   * This site will hold all Blog posts related to the Marketing product.
   */
  SHARED_BLOG_ENTERPRISE = 'SHARED_BLOG_ENTERPRISE',
  /** Standalone forms (siteless). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  STANDALONE_FORMS = 'STANDALONE_FORMS',
  /** Standalone events (siteless). MetaSites with this namespace will *not* be shown in a user's site list by default. */
  STANDALONE_EVENTS = 'STANDALONE_EVENTS',
  /** MIMIR - Siteless account for MIMIR Ai Job runner. */
  MIMIR = 'MIMIR',
  /** Wix Twins platform. */
  TWINS = 'TWINS',
  /** Wix Nano. */
  NANO = 'NANO',
  /** Base44 headless sites. */
  BASE44 = 'BASE44',
  /** Wix Channels Sites */
  CHANNELS = 'CHANNELS',
  /** Nautilus platform. */
  NAUTILUS = 'NAUTILUS',
}

/** @enumType */
export type NamespaceWithLiterals =
  | Namespace
  | 'UNKNOWN_NAMESPACE'
  | 'WIX'
  | 'SHOUT_OUT'
  | 'ALBUMS'
  | 'WIX_STORES_TEST_DRIVE'
  | 'HOTELS'
  | 'CLUBS'
  | 'ONBOARDING_DRAFT'
  | 'DEV_SITE'
  | 'LOGOS'
  | 'VIDEO_MAKER'
  | 'PARTNER_DASHBOARD'
  | 'DEV_CENTER_COMPANY'
  | 'HTML_DRAFT'
  | 'SITELESS_BUSINESS'
  | 'CREATOR_ECONOMY'
  | 'DASHBOARD_FIRST'
  | 'ANYWHERE'
  | 'HEADLESS'
  | 'ACCOUNT_MASTER_CMS'
  | 'RISE'
  | 'BRANDED_FIRST'
  | 'NOWNIA'
  | 'UGC_TEMPLATE'
  | 'CODUX'
  | 'MEDIA_DESIGN_CREATOR'
  | 'SHARED_BLOG_ENTERPRISE'
  | 'STANDALONE_FORMS'
  | 'STANDALONE_EVENTS'
  | 'MIMIR'
  | 'TWINS'
  | 'NANO'
  | 'BASE44'
  | 'CHANNELS'
  | 'NAUTILUS';

/** Site transferred to another user. */
export interface SiteTransferred {
  /**
   * A previous owner id (user that transfers meta site).
   * @format GUID
   */
  oldOwnerId?: string;
  /**
   * A new owner id (user that accepts meta site).
   * @format GUID
   */
  newOwnerId?: string;
}

/** Soft deletion of the meta site. Could be restored. */
export interface SiteDeleted {
  /** A deletion context. */
  deleteContext?: DeleteContext;
}

export interface DeleteContext {
  /** When the meta site was deleted. */
  dateDeleted?: Date | null;
  /** A status. */
  deleteStatus?: DeleteStatusWithLiterals;
  /**
   * A reason (flow).
   * @maxLength 255
   */
  deleteOrigin?: string;
  /**
   * A service that deleted it.
   * @maxLength 255
   */
  initiatorId?: string | null;
}

export enum DeleteStatus {
  UNKNOWN = 'UNKNOWN',
  TRASH = 'TRASH',
  DELETED = 'DELETED',
  PENDING_PURGE = 'PENDING_PURGE',
  PURGED_EXTERNALLY = 'PURGED_EXTERNALLY',
}

/** @enumType */
export type DeleteStatusWithLiterals =
  | DeleteStatus
  | 'UNKNOWN'
  | 'TRASH'
  | 'DELETED'
  | 'PENDING_PURGE'
  | 'PURGED_EXTERNALLY';

/** Restoration of the meta site. */
export interface SiteUndeleted {}

/** First publish of a meta site. Or subsequent publish after unpublish. */
export interface SitePublished {}

export interface SiteUnpublished {
  /**
   * A list of URLs previously associated with the meta site.
   * @maxLength 4000
   * @maxSize 10000
   */
  urls?: string[];
}

export interface SiteMarkedAsTemplate {}

export interface SiteMarkedAsWixSite {}

/**
 * Represents a service provisioned a site.
 *
 * Note on `origin_instance_id`:
 * There is no guarantee that you will be able to find a meta site using `origin_instance_id`.
 * This is because of the following scenario:
 *
 * Imagine you have a template where a third-party application (TPA) includes some stub data,
 * such as a product catalog. When you create a site from this template, you inherit this
 * default product catalog. However, if the template's product catalog is modified,
 * your site will retain the catalog as it was at the time of site creation. This ensures that
 * your site remains consistent with what you initially received and does not include any
 * changes made to the original template afterward.
 * To ensure this, the TPA on the template gets a new instance_id.
 */
export interface ServiceProvisioned {
  /**
   * Either UUID or EmbeddedServiceType.
   * @maxLength 36
   */
  appDefId?: string;
  /**
   * Not only UUID. Something here could be something weird.
   * @maxLength 36
   */
  instanceId?: string;
  /**
   * An instance id from which this instance is originated.
   * @maxLength 36
   */
  originInstanceId?: string;
  /**
   * A version.
   * @maxLength 500
   */
  version?: string | null;
  /**
   * The origin meta site id
   * @format GUID
   */
  originMetaSiteId?: string | null;
}

export interface ServiceRemoved {
  /**
   * Either UUID or EmbeddedServiceType.
   * @maxLength 36
   */
  appDefId?: string;
  /**
   * Not only UUID. Something here could be something weird.
   * @maxLength 36
   */
  instanceId?: string;
  /**
   * A version.
   * @maxLength 500
   */
  version?: string | null;
}

/** Rename of the site. Meaning, free public url has been changed as well. */
export interface SiteRenamed {
  /**
   * A new meta site name (URL slug).
   * @maxLength 20
   */
  newSiteName?: string;
  /**
   * A previous meta site name (URL slug).
   * @maxLength 255
   */
  oldSiteName?: string;
}

/**
 * Hard deletion of the meta site.
 *
 * Could not be restored. Therefore it's desirable to cleanup data.
 */
export interface SiteHardDeleted {
  /** A deletion context. */
  deleteContext?: DeleteContext;
}

export interface NamespaceChanged {
  /** A previous namespace. */
  oldNamespace?: NamespaceWithLiterals;
  /** A new namespace. */
  newNamespace?: NamespaceWithLiterals;
}

/** Assigned Studio editor */
export interface StudioAssigned {}

/** Unassigned Studio editor */
export interface StudioUnassigned {}

/**
 * Fired in case site URLs were changed in any way: new secondary domain, published, account slug rename, site rename etc.
 *
 * This is an internal event, it's not propagated in special events, because it's non-actionable. If you need to keep up
 * with sites and its urls, you need to listen to another topic/event. Read about it:
 *
 * https://bo.wix.com/wix-docs/rest/meta-site/meta-site---urls-service
 */
export interface SiteUrlChanged {}

/**
 * Used at the end of the deletion flow for both draft sites and when a user deletes a site.
 * Consumed by other teams to remove relevant data.
 */
export interface SitePurgedExternally {
  /**
   * @maxLength 2048
   * @maxSize 100
   * @deprecated
   * @targetRemovalDate 2025-04-15
   */
  appDefId?: string[];
}

/** Assigned Odeditor */
export interface OdeditorAssigned {}

/** Unassigned Odeditor */
export interface OdeditorUnassigned {}

/** Assigned Picasso editor */
export interface PicassoAssigned {}

/** Unassigned Picasso */
export interface PicassoUnassigned {}

/** Assigned Wixel */
export interface WixelAssigned {}

/** Unassigned Wixel */
export interface WixelUnassigned {}

/** Assigned StudioTwo */
export interface StudioTwoAssigned {}

/** Unassigned StudioTwo */
export interface StudioTwoUnassigned {}

/** Media from user domain is enabled. */
export interface UserDomainMediaEnabled {}

/** Media from user domain is disabled. */
export interface UserDomainMediaDisabled {}

/** Assigned Editorless */
export interface EditorlessAssigned {}

/** Unassigned Editorless */
export interface EditorlessUnassigned {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface SetDefaultBranchRequest {
  /**
   * Branch ID.
   * @format GUID
   */
  branchId: string;
}

export interface SetDefaultBranchResponse {
  /** Updated branch. */
  branch?: Branch;
}

export interface GetDefaultBranchRequest {}

export interface GetDefaultBranchResponse {
  /** Default branch. */
  branch?: Branch;
}

export interface BulkUpdateBranchTagsRequest {
  /**
   * IDs of the branches to update.
   * @minSize 1
   * @maxSize 100
   * @format GUID
   */
  branchIds: string[];
  /** Tags to assign to the branches. */
  assignTags?: PublicTags;
  /** Tags to unassign from the branches. */
  unassignTags?: PublicTags;
}

export interface BulkUpdateBranchTagsResponse {
  /**
   * Results of the bulk update operation.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateBranchTagsResult[];
  /** Metadata for the bulk operation, including counts of successes and failures. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface ItemMetadata {
  /**
   * Item ID. Should always be available, unless it's impossible to provide. For example, when failing to create an item.
   * @format GUID
   */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action succeeded for this item. When `false`, the `error` field provides failure details. */
  success?: boolean;
  /** Details about the error if the operation failed for this item. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkUpdateBranchTagsResult {
  /** Metadata about the result for a specific branch, including whether the operation succeeded. */
  itemMetadata?: ItemMetadata;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without detailed error information because the detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkUpdateBranchTagsByFilterRequest {
  /** Filter to select which branches to update. See [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language) for more information. */
  filter: Record<string, any> | null;
  /** Tags to assign to the branches. */
  assignTags?: PublicTags;
  /** Tags to unassign from the branches. */
  unassignTags?: PublicTags;
}

export interface BulkUpdateBranchTagsByFilterResponse {
  /**
   * Job ID. Pass this ID to [Get Async Job](https://dev.wix.com/docs/api-reference/business-management/async-job/get-async-job) to retrieve the status of the asynchronous operation.
   * @format GUID
   */
  jobId?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type CreateBranchApplicationErrors =
  | {
      code?: 'SOURCE_BRANCH_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'TEMPLATE_ID_NOT_SUPPORTED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'ORIGINAL_BRANCH_ALREADY_EXISTS';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'TARGET_BRANCH_SITE_HEADER_ALREADY_EXISTS';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'ILLEGAL_SOURCE_TYPE';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'SOURCE_USER_BRANCH_DOES_NOT_EXIST';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type UpdateBranchApplicationErrors = {
  code?: 'FORBIDDEN_UPDATE_ORIGINAL_BRANCH_TYPE';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type DeleteBranchApplicationErrors =
  | {
      code?: 'BRANCH_NOT_FOUND';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'FORBIDDEN_DELETE_ORIGINAL_BRANCH';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type BulkUpdateBranchTagsApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type BulkUpdateBranchTagsByFilterApplicationErrors = {
  code?: 'EMPTY_ASSIGN_AND_UNASSIGN_LISTS';
  description?: string;
  data?: Record<string, any>;
};

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface BranchCreatedEnvelope {
  entity: Branch;
  metadata: EventMetadata;
}

/**
 * Triggered when a branch is created.
 * @permissionScope Manage SEO Settings
 * @permissionScopeId SCOPE.PROMOTE.MANAGE-SEO
 * @permissionScope Manage Site Branches
 * @permissionScopeId SCOPE.DC-DOCUMENT-MANAGEMENT.MANAGE-BRANCHES
 * @permissionId EDITOR.BRANCH_READ
 * @webhook
 * @eventType wix.editor.branches.v1.branch_created
 * @slug created
 */
export declare function onBranchCreated(
  handler: (event: BranchCreatedEnvelope) => void | Promise<void>
): void;

export interface BranchDeletedEnvelope {
  entity: Branch;
  metadata: EventMetadata;
}

/**
 * Triggered when a branch is deleted.
 * @permissionScope Manage SEO Settings
 * @permissionScopeId SCOPE.PROMOTE.MANAGE-SEO
 * @permissionScope Manage Site Branches
 * @permissionScopeId SCOPE.DC-DOCUMENT-MANAGEMENT.MANAGE-BRANCHES
 * @permissionId EDITOR.BRANCH_READ
 * @webhook
 * @eventType wix.editor.branches.v1.branch_deleted
 * @slug deleted
 */
export declare function onBranchDeleted(
  handler: (event: BranchDeletedEnvelope) => void | Promise<void>
): void;

export interface BranchUpdatedEnvelope {
  entity: Branch;
  metadata: EventMetadata;
  /** @hidden */
  modifiedFields: Record<string, any>;
}

/**
 * Triggered when a branch is updated.
 * @permissionScope Manage SEO Settings
 * @permissionScopeId SCOPE.PROMOTE.MANAGE-SEO
 * @permissionScope Manage Site Branches
 * @permissionScopeId SCOPE.DC-DOCUMENT-MANAGEMENT.MANAGE-BRANCHES
 * @permissionId EDITOR.BRANCH_READ
 * @webhook
 * @eventType wix.editor.branches.v1.branch_updated
 * @slug updated
 */
export declare function onBranchUpdated(
  handler: (event: BranchUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Creates a branch.
 *
 * If a source branch isn't provided, the branch will include a blank website.
 *
 * > **Important:** Align the `sourceType` field with its corresponding source properties:
 * > - When `sourceType` is `SOURCE_BRANCH`, provide `sourceBranchProperties`.
 * > - When `sourceType` is `SOURCE_TEMPLATE`, provide `sourceTemplateProperties`.
 * >
 * > Using `UNKNOWN_SOURCE_TYPE` or mismatched properties results in an `ILLEGAL_SOURCE_TYPE` error.
 * @param branch - Branch to create.
 * @public
 * @requiredField branch
 * @permissionId EDITOR.BRANCH_CREATE
 * @applicableIdentity APP
 * @returns Created branch.
 * @fqn wix.editor.branches.v1.BranchesService.CreateBranch
 */
export async function createBranch(branch: Branch): Promise<
  NonNullablePaths<
    Branch,
    | `sourceBranchProperties.branchId`
    | `sourceTemplateProperties.templateId`
    | `type`
    | `sourceType`
    | `editorType.editorTypeOptions`
    | `tags.tags.tagIds`,
    4
  > & {
    __applicationErrorsType?: CreateBranchApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ branch: branch });

  const reqOpts = ambassadorWixEditorBranchesV1Branch.createBranch(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.branch!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { branch: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['branch']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a branch.
 * @param branchId - ID of the branch to retrieve.
 * @public
 * @requiredField branchId
 * @permissionId EDITOR.BRANCH_READ
 * @applicableIdentity APP
 * @returns Requested branch.
 * @fqn wix.editor.branches.v1.BranchesService.GetBranch
 */
export async function getBranch(
  branchId: string
): Promise<
  NonNullablePaths<
    Branch,
    | `sourceBranchProperties.branchId`
    | `sourceTemplateProperties.templateId`
    | `type`
    | `sourceType`
    | `editorType.editorTypeOptions`
    | `tags.tags.tagIds`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ branchId: branchId });

  const reqOpts = ambassadorWixEditorBranchesV1Branch.getBranch(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.branch!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { branchId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['branchId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates a branch.
 *
 * Each time the branch is updated, `revision` increments by 1. The current `revision` must be passed when updating the branch. This ensures you're working with the latest branch and prevents unintended overwrites.
 *
 * Note that `ORIGINAL_BRANCH` types can't be updated.
 * @param _id - Branch ID.
 * @public
 * @requiredField _id
 * @requiredField branch
 * @requiredField branch.revision
 * @permissionId EDITOR.BRANCH_UPDATE
 * @applicableIdentity APP
 * @returns Updated branch.
 * @fqn wix.editor.branches.v1.BranchesService.UpdateBranch
 */
export async function updateBranch(
  _id: string,
  branch: NonNullablePaths<UpdateBranch, `revision`, 2>
): Promise<
  NonNullablePaths<
    Branch,
    | `sourceBranchProperties.branchId`
    | `sourceTemplateProperties.templateId`
    | `type`
    | `sourceType`
    | `editorType.editorTypeOptions`
    | `tags.tags.tagIds`,
    4
  > & {
    __applicationErrorsType?: UpdateBranchApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    branch: { ...branch, id: _id },
  });

  const reqOpts = ambassadorWixEditorBranchesV1Branch.updateBranch(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)?.branch!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { branch: '$[1]' },
        explicitPathsToArguments: { 'branch.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'branch']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateBranch {
  /** Source branch properties. Use when `sourceType` = `SOURCE_BRANCH`. */
  sourceBranchProperties?: SourceBranch;
  /** Source template properties. Use when `sourceType` = `SOURCE_TEMPLATE`. */
  sourceTemplateProperties?: SourceTemplate;
  /**
   * Branch ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the branch is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the branch.
   * @readonly
   */
  revision?: string | null;
  /** Branch type. */
  type?: TypeWithLiterals;
  /**
   * Branch name.
   * @maxLength 50
   */
  name?: string | null;
  /** Source type for this branch's content. Must be aligned with the corresponding source properties below. */
  sourceType?: SourceTypeWithLiterals;
  /**
   * Date and time the branch was last published.
   * @readonly
   */
  lastPublishDate?: string | null;
  /**
   * Wix editor type.
   * @readonly
   */
  editorType?: EditorType;
  /**
   * Whether this branch is the default branch. The default branch is used for API operations when no branch ID is specified.
   * @readonly
   */
  default?: boolean | null;
  /**
   * Date and time the branch was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the branch was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Custom field data for the branch.
   * [Extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields) must be configured in the app dashboard before they can be accessed with API calls.
   */
  extendedFields?: ExtendedFields;
  /** Public tags assigned to the branch. */
  tags?: PublicTags;
}

/**
 * Permanently deletes a branch.
 * @param branchId - ID of the branch to delete.
 * @public
 * @requiredField branchId
 * @permissionId EDITOR.BRANCH_DELETE
 * @applicableIdentity APP
 * @fqn wix.editor.branches.v1.BranchesService.DeleteBranch
 */
export async function deleteBranch(branchId: string): Promise<
  void & {
    __applicationErrorsType?: DeleteBranchApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ branchId: branchId });

  const reqOpts = ambassadorWixEditorBranchesV1Branch.deleteBranch(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { branchId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['branchId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a list of branches, given the provided paging, filtering, and sorting.
 *
 *
 * Query Branches runs with these defaults, which you can override:
 *
 * - `updatedDate` is sorted in `DESC` order
 * - `paging.limit` is `50`
 * - `paging.offset` is `0`
 *
 * To learn how to query branches, see [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language).
 * @public
 * @permissionId EDITOR.BRANCH_READ
 * @applicableIdentity APP
 * @fqn wix.editor.branches.v1.BranchesService.QueryBranches
 */
export function queryBranches(): BranchesQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    Branch,
    'CURSOR',
    QueryBranchesRequest,
    QueryBranchesResponse
  >({
    func: async (payload: QueryBranchesRequest) => {
      const reqOpts =
        ambassadorWixEditorBranchesV1Branch.queryBranches(payload);

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryBranchesRequest['query']) => {
      const args = [query, {}] as [QueryBranchesRequest['query'], {}];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({ data }: HttpResponse<QueryBranchesResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [])
      );

      return {
        items: transformedData?.branches,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'CURSOR',
    transformationPaths: {},
  });
}

interface QueryCursorResult {
  cursors: Cursors;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface BranchesQueryResult extends QueryCursorResult {
  items: Branch[];
  query: BranchesQueryBuilder;
  next: () => Promise<BranchesQueryResult>;
  prev: () => Promise<BranchesQueryResult>;
}

export interface BranchesQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (
    propertyName:
      | 'sourceBranchProperties.branchId'
      | 'sourceBranchProperties.branchRevision'
      | 'type'
      | 'name'
      | 'lastPublishDate'
      | 'editorType'
      | 'editorType.editorTypeOptions'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => BranchesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (
    propertyName:
      | 'type'
      | 'name'
      | 'lastPublishDate'
      | 'editorType'
      | 'editorType.editorTypeOptions'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => BranchesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (
    propertyName: 'name' | 'lastPublishDate' | '_createdDate' | '_updatedDate',
    value: any
  ) => BranchesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (
    propertyName: 'name' | 'lastPublishDate' | '_createdDate' | '_updatedDate',
    value: any
  ) => BranchesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (
    propertyName: 'name' | 'lastPublishDate' | '_createdDate' | '_updatedDate',
    value: any
  ) => BranchesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (
    propertyName: 'name' | 'lastPublishDate' | '_createdDate' | '_updatedDate',
    value: any
  ) => BranchesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   */
  startsWith: (propertyName: 'name', value: string) => BranchesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasSome: (
    propertyName:
      | 'type'
      | 'name'
      | 'lastPublishDate'
      | 'editorType'
      | 'editorType.editorTypeOptions'
      | '_createdDate'
      | '_updatedDate',
    value: any[]
  ) => BranchesQueryBuilder;
  in: (
    propertyName:
      | 'type'
      | 'name'
      | 'lastPublishDate'
      | 'editorType'
      | 'editorType.editorTypeOptions'
      | '_createdDate'
      | '_updatedDate',
    value: any
  ) => BranchesQueryBuilder;
  exists: (
    propertyName:
      | 'type'
      | 'name'
      | 'lastPublishDate'
      | 'editorType'
      | 'editorType.editorTypeOptions'
      | '_createdDate'
      | '_updatedDate',
    value: boolean
  ) => BranchesQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  ascending: (
    ...propertyNames: Array<
      | 'type'
      | 'name'
      | 'lastPublishDate'
      | 'editorType'
      | 'editorType.editorTypeOptions'
      | '_createdDate'
      | '_updatedDate'
    >
  ) => BranchesQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  descending: (
    ...propertyNames: Array<
      | 'type'
      | 'name'
      | 'lastPublishDate'
      | 'editorType'
      | 'editorType.editorTypeOptions'
      | '_createdDate'
      | '_updatedDate'
    >
  ) => BranchesQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => BranchesQueryBuilder;
  /** @param cursor - A pointer to specific record */
  skipTo: (cursor: string) => BranchesQueryBuilder;
  find: () => Promise<BranchesQueryResult>;
}

/**
 * @hidden
 * @fqn wix.editor.branches.v1.BranchesService.QueryBranches
 * @requiredField query
 */
export async function typedQueryBranches(
  query: BranchQuery
): Promise<
  NonNullablePaths<
    QueryBranchesResponse,
    | `branches`
    | `branches.${number}.sourceBranchProperties.branchId`
    | `branches.${number}.sourceTemplateProperties.templateId`
    | `branches.${number}.type`
    | `branches.${number}.sourceType`
    | `branches.${number}.editorType.editorTypeOptions`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts = ambassadorWixEditorBranchesV1Branch.queryBranches(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BranchQuerySpec extends QuerySpec {
  paging: 'cursor';
  wql: [
    {
      fields: [
        '_createdDate',
        '_updatedDate',
        'editorType',
        'editorType.editorTypeOptions',
        'lastPublishDate',
        'name',
        'type'
      ];
      operators: '*';
      sort: 'BOTH';
    },
    {
      fields: [
        'sourceBranchProperties.branchId',
        'sourceBranchProperties.branchRevision'
      ];
      operators: ['$eq'];
      sort: 'NONE';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  Branch,
  BranchQuerySpec
>;
export type BranchQuery = {
  /** 
  Cursor paging options.

  Learn more about [cursor paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language#cursor-paging).  
  */
  cursorPaging?: {
    /** 
  Maximum number of items to return in the results. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonQueryWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object.

  Learn more about [filtering](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language#filters).  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object.

  Learn more about [sorting](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language#sorting). 
  @maxSize: 5 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<Branch, BranchQuerySpec, BranchQuery>(),
  },
};

/**
 * Sets the specified branch as the default.
 *
 * The default branch is used for API operations when no branch ID is specified.
 * @param branchId - Branch ID.
 * @public
 * @requiredField branchId
 * @permissionId EDITOR.SET_DEFAULT_BRANCH
 * @applicableIdentity APP
 * @fqn wix.editor.branches.v1.BranchesService.SetDefaultBranch
 */
export async function setDefaultBranch(
  branchId: string
): Promise<
  NonNullablePaths<
    SetDefaultBranchResponse,
    | `branch.sourceBranchProperties.branchId`
    | `branch.sourceTemplateProperties.templateId`
    | `branch.type`
    | `branch.sourceType`
    | `branch.editorType.editorTypeOptions`
    | `branch.tags.tags.tagIds`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ branchId: branchId });

  const reqOpts = ambassadorWixEditorBranchesV1Branch.setDefaultBranch(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { branchId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['branchId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves the default branch.
 * @public
 * @permissionId EDITOR.BRANCH_READ
 * @applicableIdentity APP
 * @fqn wix.editor.branches.v1.BranchesService.GetDefaultBranch
 */
export async function getDefaultBranch(): Promise<
  NonNullablePaths<
    GetDefaultBranchResponse,
    | `branch.sourceBranchProperties.branchId`
    | `branch.sourceTemplateProperties.templateId`
    | `branch.type`
    | `branch.sourceType`
    | `branch.editorType.editorTypeOptions`
    | `branch.tags.tags.tagIds`,
    5
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({});

  const reqOpts = ambassadorWixEditorBranchesV1Branch.getDefaultBranch(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {},
        singleArgumentUnchanged: false,
      },
      []
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates tags on multiple branches.
 *
 * Specify which tags to assign and which tags to unassign. A tag that appears in both the list to assign and the list to unassign will be assigned.
 *
 * To update a single branch's tags, call [Update Branch](https://dev.wix.com/docs/api-reference/business-management/branches/update-branch).
 * @param branchIds - IDs of the branches to update.
 * @public
 * @requiredField branchIds
 * @permissionId EDITOR.BULK_UPDATE_BRANCH_TAGS
 * @applicableIdentity APP
 * @fqn wix.editor.branches.v1.BranchesService.BulkUpdateBranchTags
 */
export async function bulkUpdateBranchTags(
  branchIds: string[],
  options?: BulkUpdateBranchTagsOptions
): Promise<
  NonNullablePaths<
    BulkUpdateBranchTagsResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  > & {
    __applicationErrorsType?: BulkUpdateBranchTagsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    branchIds: branchIds,
    assignTags: options?.assignTags,
    unassignTags: options?.unassignTags,
  });

  const reqOpts =
    ambassadorWixEditorBranchesV1Branch.bulkUpdateBranchTags(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          branchIds: '$[0]',
          assignTags: '$[1].assignTags',
          unassignTags: '$[1].unassignTags',
        },
        singleArgumentUnchanged: false,
      },
      ['branchIds', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateBranchTagsOptions {
  /** Tags to assign to the branches. */
  assignTags?: PublicTags;
  /** Tags to unassign from the branches. */
  unassignTags?: PublicTags;
}

/**
 * Asynchronously updates tags on multiple branches that match the provided filter criteria.
 *
 * Specify which tags to assign and which tags to unassign. A tag that appears both the list to assign and the list to unassign tags, will be assigned.
 *
 * To check the status of the bulk update, pass the returned `jobId` to [Get Async Job](https://dev.wix.com/docs/api-reference/business-management/async-job/get-async-job).
 *
 * To update a single branch's tags, call [Update Branch](https://dev.wix.com/docs/api-reference/business-management/branches/update-branch).
 * @param filter - Filter to select which branches to update. See [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language) for more information.
 * @public
 * @requiredField filter
 * @permissionId EDITOR.BULK_UPDATE_BRANCH_TAGS_BY_FILTER
 * @applicableIdentity APP
 * @fqn wix.editor.branches.v1.BranchesService.BulkUpdateBranchTagsByFilter
 */
export async function bulkUpdateBranchTagsByFilter(
  filter: Record<string, any>,
  options?: BulkUpdateBranchTagsByFilterOptions
): Promise<
  NonNullablePaths<BulkUpdateBranchTagsByFilterResponse, `jobId`, 2> & {
    __applicationErrorsType?: BulkUpdateBranchTagsByFilterApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    filter: filter,
    assignTags: options?.assignTags,
    unassignTags: options?.unassignTags,
  });

  const reqOpts =
    ambassadorWixEditorBranchesV1Branch.bulkUpdateBranchTagsByFilter(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          filter: '$[0]',
          assignTags: '$[1].assignTags',
          unassignTags: '$[1].unassignTags',
        },
        singleArgumentUnchanged: false,
      },
      ['filter', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateBranchTagsByFilterOptions {
  /** Tags to assign to the branches. */
  assignTags?: PublicTags;
  /** Tags to unassign from the branches. */
  unassignTags?: PublicTags;
}
